import React, { useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { fetchAuditRecordingPlaybackInfo } from '../utils/api';
import { RecordingPlaybackModal } from './RecordingPlaybackModal';

const RECORDING_CLICK_GUARD_UNTIL_KEY = '__recordingClickGuardUntil';
const RECORDING_CLICK_GUARD_WINDOW_MS = 350;
const PLAYBACK_AVAIL_TTL_MS = 60 * 1000;
const playbackAvailabilityCache = new Map();
const playbackAvailabilityPending = new Map();

const markRecordingClickGuard = () => {
  if (typeof window === 'undefined') return;
  window[RECORDING_CLICK_GUARD_UNTIL_KEY] = Date.now() + RECORDING_CLICK_GUARD_WINDOW_MS;
};

export const isAuditRecordingReady = (entry) => {
  if (!entry || typeof entry !== 'object') return false;
  const v = entry.recording_ready;
  return v === true || v === 1;
};

const normalizePlaybackPayload = (data) => {
  const rec = data?.recording;
  if (!rec?.id) {
    return { available: false, payload: null };
  }
  const seek =
    typeof data?.seek_seconds === 'number' && Number.isFinite(data.seek_seconds)
      ? Math.max(0, data.seek_seconds)
      : 0;
  return {
    available: true,
    payload: { recording: rec, seekSeconds: seek },
  };
};

const getFreshCachedPlaybackAvailability = (auditId) => {
  if (!auditId) return null;
  const cached = playbackAvailabilityCache.get(auditId);
  if (!cached) return null;
  if (Date.now() - cached.checkedAt > PLAYBACK_AVAIL_TTL_MS) return null;
  return cached;
};

const resolvePlaybackAvailability = async (auditId, { force = false } = {}) => {
  if (!auditId) return { available: false, payload: null, checkedAt: Date.now() };
  if (!force) {
    const fresh = getFreshCachedPlaybackAvailability(auditId);
    if (fresh) return fresh;
    const pending = playbackAvailabilityPending.get(auditId);
    if (pending) return pending;
  }

  const pendingPromise = (async () => {
    try {
      const data = await fetchAuditRecordingPlaybackInfo(auditId);
      const parsed = normalizePlaybackPayload(data);
      const result = { ...parsed, checkedAt: Date.now() };
      playbackAvailabilityCache.set(auditId, result);
      return result;
    } catch (err) {
      const result = { available: false, payload: null, checkedAt: Date.now() };
      playbackAvailabilityCache.set(auditId, result);
      return result;
    } finally {
      playbackAvailabilityPending.delete(auditId);
    }
  })();

  playbackAvailabilityPending.set(auditId, pendingPromise);
  return pendingPromise;
};

export const AuditRecordingPlayButton = ({
  log = null,
  enabled = true,
  playTooltip = 'Play recording from this event',
  placement = 'bottom',
  buttonSx = {},
  buttonSize = 32,
  iconSize = 22,
  showLoadingOverlay = true,
  loadingOverlaySx = {},
}) => {
  const [auditPlaybackLoading, setAuditPlaybackLoading] = useState(false);
  const [auditPlaybackOpen, setAuditPlaybackOpen] = useState(false);
  const [auditPlaybackRecording, setAuditPlaybackRecording] = useState(null);
  const [auditPlaybackSeek, setAuditPlaybackSeek] = useState(null);
  const [auditPlaybackSession, setAuditPlaybackSession] = useState(0);
  const availabilityPayloadRef= useRef(null);   

  const hasRecordingReady = Boolean(log?.audit_id) && isAuditRecordingReady(log);
  const auditId = log?.audit_id || null;
  const canAttemptPlayback = enabled && hasRecordingReady && Boolean(auditId);

  if (!canAttemptPlayback) return null;

  // Match corner controls (e.g. check-out on audit tiles): same inset from top and side via 32×32 anchor + overflow for scale(1.1).
  const handleCloseAuditPlayback = () => {
    markRecordingClickGuard();
    setAuditPlaybackOpen(false);
    setAuditPlaybackRecording(null);
    setAuditPlaybackSeek(null);
  };

  const handlePlayAuditRecording = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    markRecordingClickGuard();
    if (auditPlaybackLoading) return;
    if (!auditId) return;
    setAuditPlaybackLoading(true);
    try {
      let payload = availabilityPayloadRef.current;
      if (!payload) {
        const cached = getFreshCachedPlaybackAvailability(auditId);
        if (cached?.available && cached.payload) {
          payload = cached.payload;
        } else {
          const resolved = await resolvePlaybackAvailability(auditId);
          if (!resolved.available || !resolved.payload) return;
          payload = resolved.payload;
        }
        availabilityPayloadRef.current = payload;
      }

      const rec = payload?.recording;
      if (!rec?.id) return;

      setAuditPlaybackRecording(rec);
      setAuditPlaybackSeek(payload?.seekSeconds || 0);
      setAuditPlaybackSession((n) => n + 1);
      setAuditPlaybackOpen(true);
    } catch (err) {
      availabilityPayloadRef.current = null;
    } finally {
      setAuditPlaybackLoading(false);
    }
  };

  return (
    <>
      {showLoadingOverlay && auditPlaybackLoading && (
        <Box
          sx={{
            inset: 0,
            zIndex: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(15, 23, 42, 0.55)',
            ...loadingOverlaySx,
          }}
          data-recording-play-trigger="true"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <CircularProgress size={40} sx={{ color: '#fff' }} />
        </Box>
      )}

      <Tooltip title={playTooltip} arrow placement={placement}>
        <Box
          data-recording-play-trigger="true"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          sx={{
            zIndex: 20,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: buttonSize,
            height: buttonSize,
            overflow: 'visible',
          }}
        >
          <IconButton
            data-recording-play-trigger="true"
            size="small"
            disabled={auditPlaybackLoading}
            onClick={handlePlayAuditRecording}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              markRecordingClickGuard();
            }}
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              markRecordingClickGuard();
            }}
            sx={{
              bgcolor: 'rgba(229, 229, 229, 0.95)',
              color: '#0f172a',
              width: buttonSize,
              height: buttonSize,
              transform: 'scale(1.1)',
              borderRadius: 4,
              '&:hover': {
                bgcolor: 'rgba(229, 229, 229, 0.95)',
                transform: 'scale(1.1)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              },
              ...buttonSx,
            }}
          >
            <PlayArrow sx={{ fontSize: iconSize }} />
          </IconButton>
        </Box>
      </Tooltip>

      <RecordingPlaybackModal
        open={auditPlaybackOpen}
        recording={auditPlaybackRecording}
        onClose={handleCloseAuditPlayback}
        initialSeekSeconds={auditPlaybackSeek}
        playbackSessionId={auditPlaybackSession}
      />
    </>
  );
};

export default AuditRecordingPlayButton;
